import { useCallback, useMemo } from 'react';
import {
  RecurrentPaymentChargeDataDto,
  SubscriptionRecurringFrequency,
} from '@tiendanube/common';
import { combineStatus } from 'commons/utils/combineStatus';
import { convertStatusTypeToObject } from 'commons/utils/convertStatusTypeToObject';
import { usePlanDowngrade } from 'domains/Billing/BillingPlans/hooks';
import { useGetRecurrentPaymentChargeDataQuery } from 'domains/Billing/Checkout/CheckoutApi';
import { useGetSubscriptions } from 'domains/Billing/Checkout/hooks';
import { CONCEPTS_TO_SHOW } from './constants';

export type RecurrentPaymentChargeDisplayData =
  RecurrentPaymentChargeDataDto & {
    recurringFrequency?: SubscriptionRecurringFrequency;
    recurringInterval?: number;
    scheduledDowngradeDate?: string;
  };

function useRecurrentPaymentChargeData() {
  const {
    status: rpDataStatus,
    refetch: retryLoadRpData,
    data: recurrentPaymentChargeData,
  } = useGetRecurrentPaymentChargeDataQuery();

  const {
    status: subscriptionsStatus,
    refreshSubscriptions,
    getSubscriptionForConcept,
    subscriptions,
  } = useGetSubscriptions();

  const {
    scheduledDowngrade,
    status: downgradeStatus,
    retryLoad: retryLoadDowngrade,
  } = usePlanDowngrade(true, false);

  const { status } = combineStatus(
    rpDataStatus,
    subscriptionsStatus,
    downgradeStatus,
  );
  const retryLoad = useCallback(() => {
    retryLoadRpData();
    refreshSubscriptions();
    retryLoadDowngrade();
  }, [retryLoadRpData, refreshSubscriptions, retryLoadDowngrade]);

  const recurrentPaymentChargesData:
    | RecurrentPaymentChargeDisplayData[]
    | undefined = useMemo(() => {
    if (!recurrentPaymentChargeData || !subscriptions) return;
    const data = recurrentPaymentChargeData.filter((data) =>
      CONCEPTS_TO_SHOW.includes(data.concept),
    );
    const result: RecurrentPaymentChargeDisplayData[] = [];

    data?.forEach((data) => {
      const subscription = getSubscriptionForConcept(
        data.concept,
        data.externalReference,
      );
      if (subscription?.amountValue === 0) {
        return;
      }
      if (data.status === 'PAID' && subscription) {
        const downgradeData =
          data.concept === 'plan-cost' && scheduledDowngrade?.scheduledAt
            ? { scheduledDowngradeDate: scheduledDowngrade.scheduledAt }
            : {};
        result.push({
          ...data,
          ...subscription,
          ...downgradeData,
          id: data.id,
        });
      } else {
        result.push({
          ...data,
          recurringFrequency: subscription?.recurringFrequency,
          recurringInterval: subscription?.recurringInterval,
        });
      }
    });
    return result;
  }, [
    recurrentPaymentChargeData,
    getSubscriptionForConcept,
    subscriptions,
    scheduledDowngrade,
  ]);

  return {
    status,
    recurrentPaymentChargesData,
    ...convertStatusTypeToObject(status),
    refreshRecurrentPaymentChargeData: retryLoad,
  };
}

export { useRecurrentPaymentChargeData };
